import { DateTime } from 'luxon';
import type { ByWeekday } from 'rrule';

// Type for string-based weekday (what we use in our models)
export type WeekdayString = "MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU";

export interface RecurrenceConfig {
  start: string; // UTC ISO string
  timezone: string; // IANA timezone (e.g., "America/Edmonton")
  duration: number; // Duration in minutes
  frequency: "daily" | "weekly" | "monthly";
  interval: number;
  until?: string; // UTC ISO string
  byDay?: WeekdayString[]; // For weekly recurrence
  byMonthDay?: number[]; // For monthly recurrence
  exclusions?: {
    [exclusionId: string]: {
      timestamp: string; // UTC ISO string
    };
  };
  /**
   * Optional: Return times in local device timezone instead of UTC
   * If true, occurrences will be returned as ISO strings in the specified timezone
   * If false (default), occurrences will be in UTC (standard for storage/consistency)
   */
  returnInLocalTimezone?: boolean;
}

export interface Occurrence {
  start: string; // UTC ISO string
  end: string; // UTC ISO string
}

/**
 * Convert weekday string to Luxon weekday number (1=Monday, 7=Sunday)
 */
function weekdayToNumber(weekday: WeekdayString): number {
  const map: { [key: string]: number } = {
    "MO": 1,
    "TU": 2,
    "WE": 3,
    "TH": 4,
    "FR": 5,
    "SA": 6,
    "SU": 7,
  };
  return map[weekday] || 1;
}

/**
 * Get exclusions as array of UTC ISO strings
 */
function getExclusions(exclusions?: { [exclusionId: string]: { timestamp: string } }): string[] {
  if (!exclusions || Object.keys(exclusions).length === 0) {
    return [];
  }
  return Object.values(exclusions).map(ex => ex.timestamp);
}

/**
 * Check if a date (as UTC ISO string) is in the exclusions list
 */
function isExcluded(dateISO: string, exclusions: string[]): boolean {
  if (exclusions.length === 0) return false;
  
  // Compare dates at day level (ignore time) since exclusions are for specific dates
  const dateDay = DateTime.fromISO(dateISO).toUTC().startOf('day');
  
  return exclusions.some(exclusionISO => {
    const exclusionDay = DateTime.fromISO(exclusionISO).toUTC().startOf('day');
    return dateDay.equals(exclusionDay);
  });
}

/**
 * Generate recurring occurrences for weekly recurrence
 */
function generateWeeklyOccurrences(config: RecurrenceConfig): Occurrence[] {
  const { start, timezone, duration, byDay, interval = 1, until, exclusions } = config;
  
  if (!byDay || byDay.length === 0) {
    return [];
  }

  const occurrences: Occurrence[] = [];
  
  // Parse the start date (UTC) and convert to user's timezone
  const startUTC = DateTime.fromISO(start, { zone: 'utc' });
  const startInTimezone = startUTC.setZone(timezone);
  
  // Extract the time components (hour, minute) from the start date
  const timeComponents = {
    hour: startInTimezone.hour,
    minute: startInTimezone.minute,
    second: 0,
    millisecond: 0,
  };
  
  // Convert weekdays to numbers
  const weekdayNumbers = byDay.map(weekdayToNumber);
  
  // Parse until date if provided
  const untilDate = until ? DateTime.fromISO(until, { zone: 'utc' }).setZone(timezone) : null;
  
  // Get exclusions
  const exclusionList = getExclusions(exclusions);
  
  // Extract just the date part (without time) from start to find the base week
  const startDateOnly = startInTimezone.startOf('day').set(timeComponents);
  const startWeekday = startDateOnly.weekday; // 1=Monday, 7=Sunday
  const earliestWeekday = Math.min(...weekdayNumbers);
  
  // Find the first occurrence date - if start date is on a selected weekday, use it
  // Otherwise, find the next occurrence of the earliest selected weekday
  let firstOccurrenceDate: DateTime;
  if (weekdayNumbers.indexOf(startWeekday) !== -1) {
    // Start date is on a selected weekday, use it
    firstOccurrenceDate = startDateOnly.set(timeComponents);
  } else {
    // Calculate days until first selected weekday
    const daysUntilFirst = (earliestWeekday - startWeekday + 7) % 7;
    firstOccurrenceDate = startDateOnly.plus({ days: daysUntilFirst }).set(timeComponents);
  }
  
  // Generate occurrences up to 1 year if no until date, or until the until date
  const maxDate = untilDate || startDateOnly.plus({ years: 1 });
  const maxIterations = 365; // Safety limit
  
  let iteration = 0;
  let currentWeekStart = firstOccurrenceDate;
  
  while (currentWeekStart <= maxDate && iteration < maxIterations) {
    // For each selected weekday in this week, generate an occurrence
    for (const weekdayNum of weekdayNumbers) {
      // Calculate days from the week start (which is on earliestWeekday) to this weekday
      const daysOffset = (weekdayNum - earliestWeekday + 7) % 7;
      
      // Create occurrence date - always relative to currentWeekStart
      let occurrenceDate = currentWeekStart.plus({ days: daysOffset }).set(timeComponents);
      
      // Check if we've passed the until date
      if (untilDate && occurrenceDate > untilDate) {
        continue;
      }
      
      // Check if this occurrence is excluded
      // Always check exclusions using UTC for comparison
      const occurrenceUTC = occurrenceDate.toUTC();
      const occurrenceISO = occurrenceUTC.toISO();
      if (isExcluded(occurrenceISO, exclusionList)) {
        continue;
      }
      
      // Create occurrence
      const endDate = occurrenceDate.plus({ minutes: duration });
      
      // Return in local timezone if requested, otherwise UTC
      const startISO = config.returnInLocalTimezone 
        ? occurrenceDate.toISO() // Includes timezone offset (e.g., "2026-01-07T10:00:00-07:00")
        : occurrenceUTC.toISO(); // UTC (e.g., "2026-01-07T17:00:00.000Z")
      const endISO = config.returnInLocalTimezone
        ? endDate.toISO()
        : endDate.toUTC().toISO();
      
      occurrences.push({
        start: startISO,
        end: endISO,
      });
    }
    
    // Move to next interval week
    currentWeekStart = currentWeekStart.plus({ weeks: interval });
    iteration++;
  }
  
  // Sort by start time
  occurrences.sort((a, b) => 
    DateTime.fromISO(a.start).toMillis() - DateTime.fromISO(b.start).toMillis()
  );
  
  return occurrences;
}

/**
 * Generate recurring occurrences for daily recurrence
 */
function generateDailyOccurrences(config: RecurrenceConfig): Occurrence[] {
  const { start, timezone, duration, interval = 1, until, exclusions } = config;
  
  const occurrences: Occurrence[] = [];
  
  // Parse the start date (UTC) and convert to user's timezone
  const startUTC = DateTime.fromISO(start, { zone: 'utc' });
  const startInTimezone = startUTC.setZone(timezone);
  
  // Parse until date if provided
  const untilDate = until ? DateTime.fromISO(until, { zone: 'utc' }).setZone(timezone) : null;
  
  // Get exclusions
  const exclusionList = getExclusions(exclusions);
  
  let currentDate = startInTimezone;
  const maxDate = untilDate || startInTimezone.plus({ years: 1 });
  const maxIterations = 365; // Safety limit
  
  let iteration = 0;
  
  while (currentDate <= maxDate && iteration < maxIterations) {
    // Check if this occurrence is excluded
      // Always check exclusions using UTC for comparison
      const occurrenceUTC = currentDate.toUTC();
      const occurrenceISO = occurrenceUTC.toISO();
      if (!isExcluded(occurrenceISO, exclusionList)) {
        const endDate = currentDate.plus({ minutes: duration });
        
        // Return in local timezone if requested, otherwise UTC
        const startISO = config.returnInLocalTimezone 
          ? currentDate.toISO()
          : occurrenceUTC.toISO();
        const endISO = config.returnInLocalTimezone
          ? endDate.toISO()
          : endDate.toUTC().toISO();
        
        occurrences.push({
          start: startISO,
          end: endISO,
        });
      }
    
    // Move to next interval day
    currentDate = currentDate.plus({ days: interval });
    iteration++;
  }
  
  return occurrences;
}

/**
 * Generate recurring occurrences for monthly recurrence
 */
function generateMonthlyOccurrences(config: RecurrenceConfig): Occurrence[] {
  const { start, timezone, duration, byMonthDay, interval = 1, until, exclusions } = config;
  
  const occurrences: Occurrence[] = [];
  
  // Parse the start date (UTC) and convert to user's timezone
  const startUTC = DateTime.fromISO(start, { zone: 'utc' });
  const startInTimezone = startUTC.setZone(timezone);
  
  // Extract the time components
  const timeComponents = {
    hour: startInTimezone.hour,
    minute: startInTimezone.minute,
    second: 0,
    millisecond: 0,
  };
  
  // Parse until date if provided
  const untilDate = until ? DateTime.fromISO(until, { zone: 'utc' }).setZone(timezone) : null;
  
  // Get exclusions
  const exclusionList = getExclusions(exclusions);
  
  // Start from the start date's month
  let currentMonth = startInTimezone.startOf('month');
  const maxDate = untilDate || startInTimezone.plus({ years: 1 });
  const maxIterations = 24; // Safety limit (2 years)
  
  let iteration = 0;
  
  while (currentMonth <= maxDate && iteration < maxIterations) {
    // For each selected day of month
    if (byMonthDay && byMonthDay.length > 0) {
      for (const dayOfMonth of byMonthDay) {
        // Get the date for this day of month in the current month
        let occurrenceDate = currentMonth.set({ day: dayOfMonth });
        
        // Check if the day exists in this month (e.g., Feb 30 doesn't exist)
        if (occurrenceDate.day !== dayOfMonth) {
          continue; // Skip invalid dates
        }
        
        // Set the time
        occurrenceDate = occurrenceDate.set(timeComponents);
        
        // Skip if before start date
        if (occurrenceDate < startInTimezone) {
          continue;
        }
        
        // Check if we've passed the until date
        if (untilDate && occurrenceDate > untilDate) {
          continue;
        }
        
        // Check if this occurrence is excluded
        // Always check exclusions using UTC for comparison
        const occurrenceUTC = occurrenceDate.toUTC();
        const occurrenceISO = occurrenceUTC.toISO();
        if (isExcluded(occurrenceISO, exclusionList)) {
          continue;
        }
        
        // Create occurrence
        const endDate = occurrenceDate.plus({ minutes: duration });
        
        // Return in local timezone if requested, otherwise UTC
        const startISO = config.returnInLocalTimezone 
          ? occurrenceDate.toISO()
          : occurrenceUTC.toISO();
        const endISO = config.returnInLocalTimezone
          ? endDate.toISO()
          : endDate.toUTC().toISO();
        
        occurrences.push({
          start: startISO,
          end: endISO,
        });
      }
    }
    
    // Move to next interval month
    currentMonth = currentMonth.plus({ months: interval });
    iteration++;
  }
  
  // Sort by start time
  occurrences.sort((a, b) => 
    DateTime.fromISO(a.start).toMillis() - DateTime.fromISO(b.start).toMillis()
  );
  
  return occurrences;
}

/**
 * Generate recurring occurrences based on recurrence configuration
 * 
 * @param config Recurrence configuration
 * @returns Array of occurrence objects with start and end times in UTC ISO format
 */
export function generateRecurringOccurrences(config: RecurrenceConfig): Occurrence[] {
  switch (config.frequency) {
    case "weekly":
      return generateWeeklyOccurrences(config);
    case "daily":
      return generateDailyOccurrences(config);
    case "monthly":
      return generateMonthlyOccurrences(config);
    default:
      return [];
  }
}

